/**
 * SmartLink capacity-aware routing — operator page for feature #113.
 *
 * Layout:
 *   1. Cap editor card    — offer_id + cap_type radio (daily|monthly|lifetime)
 *                           + limit + window_key, upsert via PUT /.
 *   2. Per-offer cap list — GET /:offer_id, renders remaining vs limit as
 *                           an emerald fill that snaps to rose at 100%.
 *   3. Increment simulator— POST /increment, surfaces { over_cap, remaining,
 *                           used, cap_limit } so the operator can confirm
 *                           the router will skip the offer once it's full.
 *   4. Filter tester      — textarea of offer_ids → POST /filter → renders
 *                           kept vs dropped for a chosen (cap_type, window).
 *
 * Data: TanStack Query for fetch + invalidate, Sonner toasts for feedback.
 * Form: local state + hand-rolled `capEditorSchema.safeParse` so the lane
 * stays at four owned files (no extra deps on zod / react-hook-form).
 *
 * Brand: emerald → sky `text-brand-gradient` on the headline, Inter,
 * zinc borders, 6–8px rounded corners. Light theme only. No emojis,
 * no competitor naming.
 */

import { useEffect, useMemo, useState } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	capEditorSchema,
	CAP_TYPE_HINTS,
	CAP_TYPE_LABELS,
	CAP_TYPES,
	type CapType,
	filledPct,
	filterCappedOffers,
	type FilterResponse,
	incrementCap,
	type IncrementResponse,
	isCapType,
	listCapsForOffer,
	type OfferCapView,
	parseOfferIdList,
	remainingFor,
	smartlinkCapacityQueryKeys,
	upsertCap,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — keep classname groups DRY so the JSX stays readable.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const PRIMARY_BTN =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const SECONDARY_BTN =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const SECTION_TITLE = "text-sm font-semibold text-zinc-900";

// ---------------------------------------------------------------------------
// Props — the route passes the search-param `?offerId=` through so an
// operator opening the page from an offer page lands with the id prefilled.
// ---------------------------------------------------------------------------

export interface SmartlinkCapacityPageProps {
	initialOfferId?: string;
}

// ---------------------------------------------------------------------------
// Top-level page component.
// ---------------------------------------------------------------------------

export function SmartlinkCapacityPage(props: SmartlinkCapacityPageProps = {}) {
	const [activeOfferId, setActiveOfferId] = useState<string>(
		props.initialOfferId ?? "",
	);

	return (
		<div className="flex flex-col gap-8">
			<CapEditorCard
				initialOfferId={props.initialOfferId}
				onSavedOffer={setActiveOfferId}
			/>
			<CapListCard
				offerId={activeOfferId}
				onSelect={setActiveOfferId}
			/>
			<IncrementSimulatorCard offerId={activeOfferId} />
			<FilterTesterCard />
		</div>
	);
}

export default SmartlinkCapacityPage;

// ===========================================================================
// 1. Cap editor (PUT /)
// ===========================================================================

interface CapEditorState {
	offerId: string;
	capType: CapType;
	capLimit: string;
	windowKey: string;
}

const EMPTY_EDITOR: CapEditorState = {
	offerId: "",
	capType: "daily",
	capLimit: "",
	windowKey: "",
};

function CapEditorCard(props: {
	initialOfferId?: string;
	onSavedOffer: (offerId: string) => void;
}) {
	const queryClient = useQueryClient();
	const [form, setForm] = useState<CapEditorState>({
		...EMPTY_EDITOR,
		offerId: props.initialOfferId ?? "",
	});
	const [errors, setErrors] = useState<Record<string, string>>({});

	const mutation = useMutation({
		mutationFn: upsertCap,
		onSuccess: (row) => {
			toast.success(`Cap saved for ${row.offer_id}.`);
			queryClient.invalidateQueries({
				queryKey: smartlinkCapacityQueryKeys.byOffer(row.offer_id),
			});
			props.onSavedOffer(row.offer_id);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to save cap.");
		},
	});

	function updateField<K extends keyof CapEditorState>(
		key: K,
		value: CapEditorState[K],
	) {
		setForm((prev) => ({ ...prev, [key]: value }));
		if (errors[key]) {
			setErrors((prev) => {
				const { [key as string]: _drop, ...rest } = prev;
				return rest;
			});
		}
	}

	function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parsed = capEditorSchema.safeParse({
			offerId: form.offerId,
			capType: form.capType,
			capLimit: form.capLimit,
			windowKey: form.windowKey,
		});
		if (!parsed.success) {
			setErrors(parsed.errors);
			toast.error("Please fix the highlighted fields.");
			return;
		}
		setErrors({});
		mutation.mutate(parsed.data);
	}

	const busy = mutation.isPending;

	return (
		<section className={`${CARD} flex flex-col gap-5 p-6`}>
			<header className="flex flex-col gap-1">
				<h2 className={SECTION_TITLE}>Cap editor</h2>
				<p className={HINT}>
					Upsert a cap row. Matching rows are replaced atomically by
					(offer, cap type, window).
				</p>
			</header>

			<form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div className="flex flex-col gap-1.5">
						<label htmlFor="cap-offer-id" className={LABEL}>
							Offer ID
						</label>
						<input
							id="cap-offer-id"
							name="offerId"
							type="text"
							autoComplete="off"
							placeholder="e.g. offer_42"
							className={INPUT}
							value={form.offerId}
							onChange={(e) =>
								updateField("offerId", e.currentTarget.value)
							}
							aria-invalid={Boolean(errors.offerId)}
							aria-describedby={
								errors.offerId ? "cap-offer-id-error" : undefined
							}
						/>
						{errors.offerId && (
							<p id="cap-offer-id-error" className={ERROR_TEXT}>
								{errors.offerId}
							</p>
						)}
					</div>

					<div className="flex flex-col gap-1.5">
						<label htmlFor="cap-window-key" className={LABEL}>
							Window key
						</label>
						<input
							id="cap-window-key"
							name="windowKey"
							type="text"
							autoComplete="off"
							placeholder="2026-04-19 | 2026-04 | lifetime"
							className={INPUT}
							value={form.windowKey}
							onChange={(e) =>
								updateField("windowKey", e.currentTarget.value)
							}
							aria-invalid={Boolean(errors.windowKey)}
							aria-describedby={
								errors.windowKey ? "cap-window-key-error" : undefined
							}
						/>
						<p className={HINT}>{CAP_TYPE_HINTS[form.capType]}</p>
						{errors.windowKey && (
							<p id="cap-window-key-error" className={ERROR_TEXT}>
								{errors.windowKey}
							</p>
						)}
					</div>
				</div>

				<fieldset className="flex flex-col gap-2">
					<legend className={LABEL}>Cap type</legend>
					<div
						role="radiogroup"
						aria-label="Cap type"
						className="flex flex-wrap gap-2"
					>
						{CAP_TYPES.map((type) => (
							<CapTypeRadio
								key={type}
								type={type}
								checked={form.capType === type}
								onChange={() => updateField("capType", type)}
							/>
						))}
					</div>
					{errors.capType && (
						<p className={ERROR_TEXT}>{errors.capType}</p>
					)}
				</fieldset>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="cap-limit" className={LABEL}>
						Limit (conversions)
					</label>
					<input
						id="cap-limit"
						name="capLimit"
						type="number"
						inputMode="numeric"
						min={1}
						step={1}
						placeholder="e.g. 500"
						className={`${INPUT} max-w-xs`}
						value={form.capLimit}
						onChange={(e) =>
							updateField("capLimit", e.currentTarget.value)
						}
						aria-invalid={Boolean(errors.capLimit)}
						aria-describedby={
							errors.capLimit ? "cap-limit-error" : undefined
						}
					/>
					<p className={HINT}>
						The router skips this offer once used ≥ limit.
					</p>
					{errors.capLimit && (
						<p id="cap-limit-error" className={ERROR_TEXT}>
							{errors.capLimit}
						</p>
					)}
				</div>

				<div className="flex items-center justify-end gap-2 border-t border-zinc-200 pt-4">
					<button
						type="button"
						onClick={() => {
							setForm(EMPTY_EDITOR);
							setErrors({});
						}}
						disabled={busy}
						className={SECONDARY_BTN}
					>
						Reset
					</button>
					<button type="submit" disabled={busy} className={PRIMARY_BTN}>
						{busy ? "Saving." : "Save cap"}
					</button>
				</div>
			</form>
		</section>
	);
}

function CapTypeRadio(props: {
	type: CapType;
	checked: boolean;
	onChange: () => void;
}) {
	const base =
		"inline-flex items-center gap-2 rounded-[6px] border px-3 py-1.5 text-sm font-medium transition-colors";
	const on = "border-emerald-500 bg-emerald-50 text-emerald-700";
	const off = "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300";
	return (
		<label
			className={`${base} ${props.checked ? on : off} cursor-pointer`}
			data-testid={`cap-type-radio-${props.type}`}
		>
			<input
				type="radio"
				name="capType"
				value={props.type}
				checked={props.checked}
				onChange={props.onChange}
				className="h-3 w-3 accent-emerald-600"
			/>
			<span>{CAP_TYPE_LABELS[props.type]}</span>
		</label>
	);
}

// ===========================================================================
// 2. Cap list (GET /:offer_id)
// ===========================================================================

function CapListCard(props: {
	offerId: string;
	onSelect: (offerId: string) => void;
}) {
	const [search, setSearch] = useState(props.offerId);

	const trimmed = search.trim();
	const query = useQuery({
		queryKey: smartlinkCapacityQueryKeys.byOffer(trimmed),
		queryFn: () => listCapsForOffer(trimmed),
		enabled: trimmed.length > 0,
	});

	function handleLoad(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (trimmed.length === 0) {
			toast.error("Enter an offer ID first.");
			return;
		}
		props.onSelect(trimmed);
		query.refetch();
	}

	const rows = query.data ?? [];

	return (
		<section className={`${CARD} flex flex-col gap-5 p-6`}>
			<header className="flex flex-col gap-1">
				<h2 className={SECTION_TITLE}>Caps for offer</h2>
				<p className={HINT}>
					Remaining vs limit for every cap row on this offer. Bars fill
					emerald, flipping rose the moment used hits the limit.
				</p>
			</header>

			<form onSubmit={handleLoad} className="flex flex-wrap items-end gap-3">
				<div className="flex min-w-[240px] flex-1 flex-col gap-1.5">
					<label htmlFor="cap-list-offer" className={LABEL}>
						Offer ID
					</label>
					<input
						id="cap-list-offer"
						type="text"
						autoComplete="off"
						placeholder="e.g. offer_42"
						className={INPUT}
						value={search}
						onChange={(e) => setSearch(e.currentTarget.value)}
					/>
				</div>
				<button type="submit" className={SECONDARY_BTN}>
					{query.isFetching ? "Loading." : "Load caps"}
				</button>
			</form>

			{query.isError && (
				<p className={ERROR_TEXT}>
					{(query.error as Error)?.message || "Unable to load caps."}
				</p>
			)}

			{trimmed.length === 0 ? (
				<p className={HINT}>Enter an offer ID and press Load caps.</p>
			) : query.isSuccess && rows.length === 0 ? (
				<p className={HINT}>
					No caps on file. This offer is unlimited until a cap is set.
				</p>
			) : (
				<ul className="flex flex-col gap-3">
					{rows.map((row) => (
						<CapRow key={`${row.cap_type}|${row.window_key}`} row={row} />
					))}
				</ul>
			)}
		</section>
	);
}

function CapRow(props: { row: OfferCapView }) {
	const pct = filledPct(props.row);
	const remaining = remainingFor(props.row);
	const full = pct >= 100;
	const barColor = full ? "bg-rose-500" : "bg-emerald-500";

	return (
		<li className="rounded-[6px] border border-zinc-200 bg-white p-3">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<div className="flex items-center gap-2">
					<span className="rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-700">
						{CAP_TYPE_LABELS[props.row.cap_type]}
					</span>
					<code className="text-xs text-zinc-600">
						{props.row.window_key}
					</code>
				</div>
				<div className="text-xs text-zinc-600">
					{props.row.used.toLocaleString()} /{" "}
					{props.row.cap_limit.toLocaleString()}{" "}
					<span className={full ? "text-rose-600" : "text-emerald-700"}>
						({remaining.toLocaleString()} left)
					</span>
				</div>
			</div>
			<div
				className="mt-2 h-2 w-full overflow-hidden rounded-full bg-zinc-100"
				role="progressbar"
				aria-valuemin={0}
				aria-valuemax={100}
				aria-valuenow={pct}
				aria-label={`Cap usage ${pct}%`}
			>
				<div
					className={`h-full ${barColor} transition-[width] duration-200 ease-out`}
					style={{ width: `${Math.min(100, pct)}%` }}
				/>
			</div>
		</li>
	);
}

// ===========================================================================
// 3. Increment simulator (POST /increment)
// ===========================================================================

interface IncState {
	offerId: string;
	capType: CapType;
	windowKey: string;
	qty: string;
}

function IncrementSimulatorCard(props: { offerId: string }) {
	const queryClient = useQueryClient();
	const [form, setForm] = useState<IncState>({
		offerId: props.offerId,
		capType: "daily",
		windowKey: "",
		qty: "1",
	});
	const [last, setLast] = useState<IncrementResponse | null>(null);

	// Keep the offer id in sync when the operator selects a different offer
	// via the cap editor / list cards.
	useEffect(() => {
		setForm((prev) =>
			prev.offerId === props.offerId
				? prev
				: { ...prev, offerId: props.offerId },
		);
	}, [props.offerId]);

	const mutation = useMutation({
		mutationFn: incrementCap,
		onSuccess: (res) => {
			setLast(res);
			toast.success(
				res.over_cap
					? `Over cap — router will skip ${res.offer_id}.`
					: `Bumped ${res.offer_id}: ${res.remaining} left.`,
			);
			queryClient.invalidateQueries({
				queryKey: smartlinkCapacityQueryKeys.byOffer(res.offer_id),
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to increment.");
		},
	});

	function handleRun(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const offerId = form.offerId.trim();
		const windowKey = form.windowKey.trim();
		const qty = Number.parseInt(form.qty, 10);
		if (!offerId) {
			toast.error("Enter an offer ID.");
			return;
		}
		if (!windowKey) {
			toast.error("Enter a window key.");
			return;
		}
		if (!Number.isInteger(qty) || qty < 1) {
			toast.error("Qty must be a positive integer.");
			return;
		}
		mutation.mutate({
			offer_id: offerId,
			cap_type: form.capType,
			window_key: windowKey,
			qty,
		});
	}

	const busy = mutation.isPending;

	return (
		<section className={`${CARD} flex flex-col gap-5 p-6`}>
			<header className="flex flex-col gap-1">
				<h2 className={SECTION_TITLE}>Increment simulator</h2>
				<p className={HINT}>
					Fire a fake conversion to test over-cap behaviour end to end.
				</p>
			</header>

			<form onSubmit={handleRun} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				<div className="flex flex-col gap-1.5">
					<label htmlFor="inc-offer-id" className={LABEL}>
						Offer ID
					</label>
					<input
						id="inc-offer-id"
						type="text"
						className={INPUT}
						value={form.offerId}
						onChange={(e) =>
							setForm((prev) => ({
								...prev,
								offerId: e.currentTarget.value,
							}))
						}
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="inc-window" className={LABEL}>
						Window key
					</label>
					<input
						id="inc-window"
						type="text"
						className={INPUT}
						value={form.windowKey}
						onChange={(e) =>
							setForm((prev) => ({
								...prev,
								windowKey: e.currentTarget.value,
							}))
						}
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="inc-cap-type" className={LABEL}>
						Cap type
					</label>
					<select
						id="inc-cap-type"
						className={INPUT}
						value={form.capType}
						onChange={(e) => {
							const next = e.currentTarget.value;
							if (isCapType(next)) {
								setForm((prev) => ({ ...prev, capType: next }));
							}
						}}
					>
						{CAP_TYPES.map((t) => (
							<option key={t} value={t}>
								{CAP_TYPE_LABELS[t]}
							</option>
						))}
					</select>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="inc-qty" className={LABEL}>
						Qty
					</label>
					<input
						id="inc-qty"
						type="number"
						min={1}
						step={1}
						className={INPUT}
						value={form.qty}
						onChange={(e) =>
							setForm((prev) => ({ ...prev, qty: e.currentTarget.value }))
						}
					/>
				</div>

				<div className="sm:col-span-2 flex justify-end border-t border-zinc-200 pt-4">
					<button type="submit" disabled={busy} className={PRIMARY_BTN}>
						{busy ? "Running." : "Run increment"}
					</button>
				</div>
			</form>

			{last && <IncrementResult result={last} />}
		</section>
	);
}

function IncrementResult(props: { result: IncrementResponse }) {
	const pct = filledPct(props.result);
	const full = props.result.over_cap || pct >= 100;
	const tone = full
		? "border-rose-200 bg-rose-50 text-rose-900"
		: "border-emerald-200 bg-emerald-50 text-emerald-900";
	return (
		<div className={`rounded-[6px] border p-3 text-sm ${tone}`}>
			<p className="font-medium">
				{full ? "Over cap — router will skip." : "Within cap."}
			</p>
			<dl className="mt-2 grid grid-cols-2 gap-y-1 text-xs sm:grid-cols-4">
				<div className="flex flex-col">
					<dt className="text-zinc-600">Used</dt>
					<dd className="font-mono">{props.result.used.toLocaleString()}</dd>
				</div>
				<div className="flex flex-col">
					<dt className="text-zinc-600">Limit</dt>
					<dd className="font-mono">
						{props.result.cap_limit.toLocaleString()}
					</dd>
				</div>
				<div className="flex flex-col">
					<dt className="text-zinc-600">Remaining</dt>
					<dd className="font-mono">
						{props.result.remaining.toLocaleString()}
					</dd>
				</div>
				<div className="flex flex-col">
					<dt className="text-zinc-600">Filled</dt>
					<dd className="font-mono">{pct}%</dd>
				</div>
			</dl>
		</div>
	);
}

// ===========================================================================
// 4. Filter tester (POST /filter)
// ===========================================================================

interface FilterTesterState {
	raw: string;
	capType: CapType;
	windowKey: string;
}

function FilterTesterCard() {
	const [form, setForm] = useState<FilterTesterState>({
		raw: "",
		capType: "daily",
		windowKey: "",
	});
	const [result, setResult] = useState<{
		kept: string[];
		dropped: string[];
		window_key: string;
		cap_type: CapType;
	} | null>(null);

	const parsed = useMemo(() => parseOfferIdList(form.raw), [form.raw]);

	const mutation = useMutation({
		mutationFn: filterCappedOffers,
		onSuccess: (res: FilterResponse, args) => {
			const keptSet = new Set(res.offer_ids);
			const dropped = args.offer_ids.filter((id) => !keptSet.has(id));
			setResult({
				kept: res.offer_ids,
				dropped,
				window_key: res.window_key,
				cap_type: res.cap_type,
			});
			toast.success(
				`Kept ${res.offer_ids.length} of ${args.offer_ids.length}.`,
			);
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to run filter.");
		},
	});

	function handleRun(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (parsed.ids.length === 0) {
			toast.error("Paste at least one offer ID.");
			return;
		}
		const windowKey = form.windowKey.trim();
		if (!windowKey) {
			toast.error("Enter a window key.");
			return;
		}
		mutation.mutate({
			offer_ids: parsed.ids,
			cap_type: form.capType,
			window_key: windowKey,
		});
	}

	const busy = mutation.isPending;

	return (
		<section className={`${CARD} flex flex-col gap-5 p-6`}>
			<header className="flex flex-col gap-1">
				<h2 className={SECTION_TITLE}>Filter tester</h2>
				<p className={HINT}>
					Simulate the router's pre-pick step. Paste offer IDs (comma
					or whitespace separated) and see which survive the cap gate.
				</p>
			</header>

			<form onSubmit={handleRun} className="flex flex-col gap-4">
				<div className="flex flex-col gap-1.5">
					<label htmlFor="filter-raw" className={LABEL}>
						Offer IDs
					</label>
					<textarea
						id="filter-raw"
						rows={4}
						className={`${INPUT} font-mono`}
						placeholder="offer_1, offer_2, offer_3"
						value={form.raw}
						onChange={(e) =>
							setForm((prev) => ({ ...prev, raw: e.currentTarget.value }))
						}
					/>
					<p className={HINT}>
						{parsed.ids.length} unique ID{parsed.ids.length === 1 ? "" : "s"}
						{parsed.dropped > 0
							? ` — ${parsed.dropped} dropped for being too long.`
							: "."}
					</p>
				</div>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div className="flex flex-col gap-1.5">
						<label htmlFor="filter-cap-type" className={LABEL}>
							Cap type
						</label>
						<select
							id="filter-cap-type"
							className={INPUT}
							value={form.capType}
							onChange={(e) => {
								const next = e.currentTarget.value;
								if (isCapType(next)) {
									setForm((prev) => ({ ...prev, capType: next }));
								}
							}}
						>
							{CAP_TYPES.map((t) => (
								<option key={t} value={t}>
									{CAP_TYPE_LABELS[t]}
								</option>
							))}
						</select>
					</div>

					<div className="flex flex-col gap-1.5">
						<label htmlFor="filter-window" className={LABEL}>
							Window key
						</label>
						<input
							id="filter-window"
							type="text"
							className={INPUT}
							value={form.windowKey}
							onChange={(e) =>
								setForm((prev) => ({
									...prev,
									windowKey: e.currentTarget.value,
								}))
							}
						/>
					</div>
				</div>

				<div className="flex justify-end border-t border-zinc-200 pt-4">
					<button type="submit" disabled={busy} className={PRIMARY_BTN}>
						{busy ? "Running." : "Run filter"}
					</button>
				</div>
			</form>

			{result && <FilterResult result={result} total={parsed.ids.length} />}
		</section>
	);
}

function FilterResult(props: {
	result: {
		kept: string[];
		dropped: string[];
		window_key: string;
		cap_type: CapType;
	};
	total: number;
}) {
	return (
		<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
			<BucketList
				title="Kept"
				tone="emerald"
				ids={props.result.kept}
				total={props.total}
			/>
			<BucketList
				title="Dropped"
				tone="rose"
				ids={props.result.dropped}
				total={props.total}
			/>
		</div>
	);
}

function BucketList(props: {
	title: string;
	tone: "emerald" | "rose";
	ids: string[];
	total: number;
}) {
	const headerTone =
		props.tone === "emerald" ? "text-emerald-700" : "text-rose-700";
	const border =
		props.tone === "emerald" ? "border-emerald-200" : "border-rose-200";
	const bg = props.tone === "emerald" ? "bg-emerald-50" : "bg-rose-50";
	return (
		<div className={`rounded-[6px] border ${border} ${bg} p-3`}>
			<div className="flex items-center justify-between">
				<p className={`text-xs font-semibold uppercase tracking-[0.12em] ${headerTone}`}>
					{props.title}
				</p>
				<p className="text-xs text-zinc-600">
					{props.ids.length} / {props.total}
				</p>
			</div>
			{props.ids.length === 0 ? (
				<p className="mt-2 text-xs text-zinc-500">None.</p>
			) : (
				<ul className="mt-2 flex max-h-40 flex-col gap-1 overflow-y-auto font-mono text-xs text-zinc-700">
					{props.ids.map((id) => (
						<li key={id} className="truncate">
							{id}
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
